<script setup>
import { visualMode, hasLottie } from '~/utils/block-visual.js'

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

// Computed
const positionClass = computed(() => {
  const position = props.block?.position

  if (position === 'center') {
    return '--image-center'
  }

  if (position === 'imageLeft') {
    return '--image-left'
  }

  if (position === 'fullWidth') {
    return '--full-width'
  }

  // Default: imageRight
  return '--image-right'
})

const hasBackgroundClass = computed(() => {
  const bgColor = props.block?.backgroundColor
  return bgColor && bgColor.toLowerCase().trim() !== '#ffffff'
})

// Tips: build from the numbered CMS fields and keep only those with content
// (icon, heading or description). Supports up to 4.
const tips = computed(() => {
  const b = props.block || {}
  return [1, 2, 3, 4].map((n) => ({
    key: n,
    icon: b[`tipIcon${n}`]?.[0]?.url || null,
    heading: b[`tipHeading${n}`] || '',
    description: b[`tipDescription${n}`] || ''
  })).filter((t) => t.icon || t.heading || t.description)
})

// Switch to a 4-column layout only when the 4th tip itself has content;
// 1–3 tips keep the existing 3-column behaviour.
const hasFourthTip = computed(() => {
  const t = tips.value
  return t.length === 4 && t.some((tip) => tip.key === 4)
})

// CTA button (Craft "button" link field)
const button = computed(() => props.block?.button || null)

const assetDesktop = computed(() => {
  const asset = props.block?.assetDesktop
  if (!asset) return null

  const assetObj = Array.isArray(asset) ? asset[0] : asset
  return assetObj || null
})

const isVideo = computed(() => {
  const url = assetDesktop.value?.url
  if (!url) return false

  const videoExtensions = ['.mp4', '.webm', '.mov', '.ogg', '.avi']
  const lowerUrl = url.toLowerCase()
  return videoExtensions.some(ext => lowerUrl.includes(ext))
})

const isImage = computed(() => {
  const url = assetDesktop.value?.url
  if (!url) return false

  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']
  const lowerUrl = url.toLowerCase()
  return imageExtensions.some(ext => lowerUrl.includes(ext))
})

const videoContainerRef = ref(null)
const videoEl = ref(null)
const isPaused = ref(false)

const syncVideoEl = async () => {
  if (!isVideo.value) return
  await nextTick()

  const container = videoContainerRef.value
  const el = container?.querySelector?.('video') || null
  videoEl.value = el
  isPaused.value = Boolean(el?.paused)
}

const onVideoPlay = () => {
  isPaused.value = false
}

const onVideoPause = () => {
  isPaused.value = true
}

const attachVideoListeners = () => {
  const el = videoEl.value
  if (!el) return
  el.addEventListener('play', onVideoPlay)
  el.addEventListener('pause', onVideoPause)
}

const detachVideoListeners = () => {
  const el = videoEl.value
  if (!el) return
  el.removeEventListener('play', onVideoPlay)
  el.removeEventListener('pause', onVideoPause)
}

const pauseVideo = () => {
  videoEl.value?.pause?.()
}

const playVideo = async () => {
  const el = videoEl.value
  if (!el?.play) return
  try {
    await el.play()
  } catch {
    // Autoplay policies may block play without user gesture; click is a gesture,
    // but we still guard to avoid unhandled promise rejection.
  }
}

watch(isVideo, async (value) => {
  detachVideoListeners()
  videoEl.value = null
  isPaused.value = false

  if (!value) return
  await syncVideoEl()
  attachVideoListeners()
}, { immediate: true })

onMounted(async () => {
  if (!isVideo.value) return
  await syncVideoEl()
  attachVideoListeners()
})

onBeforeUnmount(() => {
  detachVideoListeners()
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section
  :class="['section-mb text-and-media --delight', positionClass, { '--has-background': hasBackgroundClass }]"
  :style="{ backgroundColor: hasBackgroundClass ? block.backgroundColor : 'transparent' }"
  :id="block?.['anchorId']"
>
  <div class="container flex flex-col" :class="`--position-${block.textContentPosition}`">
    <div class="text-and-media__content"
      :class="[ `--ha-mob-${block.headAlignMobile}`, `--ha-desk-${block.headAlignDesktop}` ]">
      <div v-html="block.heading?.html"
          class="text-and-media__heading"></div>

      <div class="text-and-media__body"
        v-if="block.description?.length || button?.url && button?.title">
        <p v-if="block.description?.length"
            class="text-and-media__description">
          {{ block.description }}
        </p>

        <GlobalsButton
          v-if="button?.url && button?.title"
          :url="button.url"
          :title="button.title"
          :type="block?.buttonSettings?.linkType"
          :size="block?.buttonSettings?.linkSize"
          :icon="block?.buttonSettings?.linkIcon"
          :popup-enabled="Boolean(block?.buttonSettings?.popupEnabled)"
          class="text-and-media__button"
        />
      </div>

      <div v-if="block.table && block.table.length"
        class="table-desktop">
        <div v-for="item in block.table" :key="item.id" class="table-desktop__item">
          <div v-if="item.label?.length" class="table__label">
            {{ item.label }}
          </div>
          <div class="table-desktop__body">
            <div class="table__body-title">{{ item.title }}</div>
            <div class="table-desktop__body-description">{{ item.description }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="assetDesktop?.url || (visualMode(block) === 'lottie' && hasLottie(block))"
      class="text-and-media__asset"
      :class="{ '--has-conversation-box': visualMode(block) === 'kv' && block.conversationBox?.length }"
    >

      <div v-if="visualMode(block) === 'kv' && block.conversationBox?.length"
        class="text-and-media__conversation-box"
      >
        <GlobalsIsolatedHtml
          :html="block.conversationBox[0].html"
          class="text-and-media__conversation-box-content"
        />
      </div>

      <GlobalsLottieVisual v-if="visualMode(block) === 'lottie' && hasLottie(block)" :entry="block" desktop-field="assetDesktop" mobile-field="assetMobile" :width="block.position === 'fullWidth' ? 1552 : 866" :height="block.position === 'fullWidth' ? 584 : 660" />
      <div v-else-if="isVideo"
        class="text-and-media__asset-video">
        <div ref="videoContainerRef">
        <GlobalsVideo
          :video="assetDesktop"
        />
        </div>
        <div class="text-and-media__asset-video-overlay">
          <button
            type="button"
            aria-label="Pause video"
            :aria-disabled="isPaused ? 'true' : 'false'"
            :disabled="isPaused"
            @click="pauseVideo"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
              <foreignObject x="-15.7224" y="-15.7224" width="57.4449" height="57.4449"><div xmlns="http://www.w3.org/1999/xhtml" style="backdrop-filter:blur(7.86px);clip-path:url(#bgblur_0_272_5708_clip_path);height:100%;width:100%"></div></foreignObject><circle data-figma-bg-blur-radius="15.7224" cx="13" cy="13" r="12.5" fill="white" stroke="black"/>
              <rect x="9" y="8" width="3" height="10" rx="1" fill="black"/>
              <rect x="14" y="8" width="3" height="10" rx="1" fill="black"/>
              <defs>
                <clipPath id="bgblur_0_272_5708_clip_path" transform="translate(15.7224 15.7224)"><circle cx="13" cy="13" r="12.5"/>
              </clipPath></defs>
            </svg>
          </button>

          <button
            type="button"
            aria-label="Play video"
            :aria-disabled="isPaused ? 'false' : 'true'"
            :disabled="!isPaused"
            @click="playVideo"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
              <foreignObject x="-15.7224" y="-15.7224" width="57.4449" height="57.4449"><div xmlns="http://www.w3.org/1999/xhtml" style="backdrop-filter:blur(7.86px);clip-path:url(#bgblur_0_272_5705_clip_path);height:100%;width:100%"></div></foreignObject><circle data-figma-bg-blur-radius="15.7224" cx="13" cy="13" r="12.5" fill="white" stroke="black"/>
              <path d="M18.5 12.134C19.1667 12.5189 19.1667 13.4811 18.5 13.866L11 18.1962C10.3333 18.5811 9.5 18.0999 9.5 17.3301L9.5 8.66987C9.5 7.90007 10.3333 7.41895 11 7.80385L18.5 12.134Z" fill="black"/>
              <defs>
                <clipPath id="bgblur_0_272_5705_clip_path" transform="translate(15.7224 15.7224)"><circle cx="13" cy="13" r="12.5"/>
              </clipPath></defs>
            </svg>
          </button>
        </div>
      </div>

      <GlobalsImage
        v-else-if="isImage"
        :landscape="assetDesktop"
        :portrait="block.assetMobile"
        :max-width="['fullWidth', 'center'].includes(props.block?.position) ? null : 900"
      />
    </div>
  </div>

  <div v-if="block.table && block.table.length"
    class="container table-mobile">
    <div v-for="item in block.table" :key="item.id" class="table-mobile__item">
      <div v-if="item.label?.length" class="table__label">
        {{ item.label }}
      </div>
      <div class="table-mobile__body">
        <div class="table__body-title">{{ item.title }}</div>
        <div class="table-mobile__body-description">{{ item.description }}</div>
      </div>
    </div>
  </div>


  <!-- Tips -->
  <div v-if="block.extendWithTips && tips.length"
    class="container tips"
    :class="{ '--four-up': hasFourthTip }">

    <div v-for="tip in tips" :key="tip.key" class="tip"
      :class="{ '--has-bg': block.tipsBackground && block.tipsBackground !== '#ffffff' }"
      :style="{ backgroundColor: block.tipsBackground || 'transparent' }">
      <div v-if="tip.icon" class="tip__icon">
        <img :src="tip.icon" :alt="tip.heading" loading="lazy" width="32" height="32" />
      </div>

      <p v-if="tip.heading" class="tip__title">{{ tip.heading }}</p>
      <p v-if="tip.description" class="tip__description">{{ tip.description }}</p>
    </div>
  </div>
  <!-- End of Tips -->
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.--has-background {
  padding-block: 64px;
}

.text-and-media__content {
  margin-bottom: 32px;
}

.text-and-media__heading :deep(*) {
  font-family: var(--font-family-heading);
  font-size: 28px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.56px;
  color: var(--color-gray-800);

  @media (min-width: 1440px) {
    font-size: 38px;
    letter-spacing: -0.76px;
  }
}

.text-and-media__description {
  font-size: 16px;
  line-height: 140%; /* 22.4px */
  letter-spacing: -0.32px;
  color: var(--color-gray-500);
  margin-top: 16px;

  @media (min-width: 1440px) {
    font-size: 20px;
    line-height: 140%; /* 29.2px */
    letter-spacing: -0.4px;
  }
}

/* White CTA button: same as the global primary button, but white background,
   black text and a black border (hover/arrow inherit from the global styles —
   black text means the hover arrow is black via currentColor). */
.text-and-media__button {
  margin-top: 24px;
  /* align-self: flex-start;
  border: 1px solid var(--color-black);
  color: var(--color-black); */

  @media (min-width: 768px) {
    margin-top: 32px;
  }
}

.text-and-media__asset :deep(img) {
  border-radius: 16px;
}

.tips {
  display: flex;
  flex-direction: column;
  margin-top: 32px;
}

.tip {
  display: flex;
  flex-direction: column;
}

.tip + .tip {
  margin-top: 28px;
}

.tip__icon {
  display: flex;
  width: 32px;
  margin-bottom: 12px;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
}

.tip .tip__title {
  font-size: 20px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.4px;
  color: var(--color-gray-700);
  margin-bottom: 8px;
}

.tip__description {
  line-height: 140%;
  letter-spacing: -0.32px;
  color: var(--color-gray-400);
}

@media (min-width: 768px) {
  .tips {
    flex-direction: row;
    margin-top: 40px;
  }

  .tip {
    width: calc(33.333% - 24px / 3 * 2);
  }

  .tip + .tip {
    margin-top: 0;
    margin-left: 24px;
  }

  /* 4-column layout when a 4th tip has content */
  .tips.--four-up .tip {
    width: calc(25% - 16px / 4 * 3);
  }

  .tips.--four-up .tip + .tip {
    margin-left: 16px;
  }

  .tip__icon {
    margin-bottom: 8px;
  }

  .tip .tip__title {
    margin-bottom: 12px;
  }

  .tip__description {
    font-size: 15px;
    line-height: 146%;
    letter-spacing: -0.3px;
  }
}

@media (min-width: 1024px) {
  .--image-right, .--image-left {
    .container {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }

    .container.--position-top {
      align-items: stretch;
    }

    .text-and-media__content {
      width: calc(573 / 1552 * 100%);
      max-width: 572px;
      margin-bottom: 0;
      display: flex;
      flex-direction: column;
    }

    .text-and-media__asset {
      width: calc(867 / 1552 * 100%);
      max-width: 866px;
    }
  }

  .--image-left .container {
    flex-direction: row-reverse;
  }

  .text-and-media__description {
    margin-top: 24px;
  }

  .--full-width .text-and-media__content {
    display: flex;
    justify-content: space-between;
    margin-bottom: 48px;

    .text-and-media__heading {
      margin-bottom: 0;
    }

    .text-and-media__body {
      width: calc(572 / 1552 * 100%);
    }

    .text-and-media__description {
      width: 100%;
      margin-top: 0;
    }
  }

  .--has-background {
    padding-block: 96px;
  }
}

.text-and-media__asset-video {
  position: relative;
}

.text-and-media__asset-video-overlay {
  position: absolute;
  right: 24px;
  bottom: 24px;
  display: flex;
  gap: 12px;
}

.table-mobile {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    display: none;
  }
}

.table-mobile__item {
  display: flex;
  gap: 16px;
}

.table__label {
  width: 45px;
  height: 22px;
  border-radius: 6px;
  background: #F2FF66;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 138%; /* 17.94px */
  letter-spacing: -0.26px;
  color: var(--color-gray-900);
}

.table-mobile__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-grow: 1;
}

.table__body-title {
  font-size: 20px;
  font-weight: 500;
  line-height: 120%; /* 24px */
  letter-spacing: -0.4px;
  color: var(--color-gray-700);
}

.table-mobile__body-description {
  line-height: 146%; /* 23.36px */
  letter-spacing: -0.32px;
  color: var(--color-gray-400);
}

.table-desktop {
  display: none;

  @media (min-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: auto;
    padding-top: 32px;

    .table-desktop__item {
      display: flex;
      gap: 16px;
    }

    .table-desktop__body {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex-grow: 1;
    }

    .table-desktop__body-description {
      font-size: 18px;
      line-height: 138%; /* 24.84px */
      letter-spacing: -0.36px;
      color: var(--color-gray-500);
    }
  }
}

.tip.--has-bg {
  border-radius: 16px;
  padding: 24px;

  .tip__icon {
    width: 40px;

    @media (min-width: 1440px) {
      margin-bottom: 16px;
    }
  }

  .tip__title {
    margin-bottom: 16px;

    @media (min-width: 1440px) {
      font-size: 22px;
      line-height: 144%; /* 31.68px */
      letter-spacing: -0.44px;
      color: var(--color-gray-800);
      margin-bottom: 12px;
    }
  }

  .tip__description {
    @media (min-width: 1440px) {
      font-size: 16px;
      line-height: 140%; /* 22.4px */
      letter-spacing: -0.32px;
    }
  }

  @media (min-width: 1440px) {
    padding: 32px 36px;
  }
}


/* Image center */
.text-and-media.--image-center {
  .text-and-media__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    @media (min-width: 768px) {
      max-width: 1016px;
      margin-inline: auto;
    }

    @media (min-width: 1024px) {
      margin-bottom: 48px;
    }
  }

  .text-and-media__asset {
    display: flex;
    justify-content: center;

    @media (min-width: 1024px) {
      & :deep(img) {
        max-width: 836px;
        object-fit: contain;
        border-radius: 0;
      }
    }
  }
}


/* Head position */
.text-and-media.--image-center,
.text-and-media.--full-width {

  &:not(:has(.text-and-media__asset)) .text-and-media__content {
    margin-bottom: 0 !important;
  }

  .text-and-media__content.--ha-mob-left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .text-and-media__content.--ha-mob-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  @media (min-width: 1024px) {

    .text-and-media__content.--ha-desk-left {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
    }

    .text-and-media__content.--ha-desk-center {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }
    
    .text-and-media__content.--ha-mob-two-columns,
    .text-and-media__content.--ha-desk-two-columns {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      text-align: left;

      .text-and-media__heading {
        width: 55%;
        margin-bottom: 0;
      }

      .text-and-media__body {
        width: 40%;
        max-width: 571px;
        margin-top: 0;
      }
    }

  }
}




.text-and-media__asset.--has-conversation-box {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
}



.text-and-media__asset.--has-conversation-box :deep(img) {
  position: absolute;
  inset: 0;
  margin: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center;
}

.text-and-media__conversation-box-content {
  position: relative;
  z-index: 1;
}

</style>
